import { FlowFrame } from '@/routes/_app/-components/shared/flow-frame'
import { Frame } from '@/components/ui/frame'
import { Skeleton } from '@/components/ui/skeleton'
import { RANK_ROW_CLASS } from '@/routes/_app/-components/applicants/test-rank-item'
import { AuthLayout } from '@/routes/_app/-components/auth/auth-layout'

// Route `pendingComponent`s for the register/test onboarding flow.
//
// Every route in the flow navigates with `viewTransition: flowViewTransition()`, so for the morph to read as one
// motion these pending views MUST reuse the SAME structure as the loaded route: the outer wrapper, the shared
// `flow-header` view-transition name (app.css), and the same <FlowFrame>. With that, a slow loader morphs
// old → skeleton, and the skeleton → content swap is a plain React update inside the same box.

/** The same centered column the flow routes render their content into (section > inner div). */
function FlowShell(props: { children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <div className="mx-auto flex w-full max-w-120 flex-col items-center gap-6 px-4 py-6">{props.children}</div>
    </section>
  )
}

/** Placeholder for the RegistrationSteps timeline (a row of thin pills). The real step count depends on
 *  the user data the route is still loading, so we paint a neutral fixed set of muted bars. */
function FlowStepsSkeleton() {
  return (
    <div className="mx-auto flex w-full max-w-sm items-center gap-1.5">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="bg-muted h-1.5 flex-1 rounded-full" />
      ))}
    </div>
  )
}

/** Title + subtitle above the box, carrying the shared `flow-header` view-transition name. */
function FlowHeaderSkeleton() {
  return (
    <div className="flow-header flex flex-col items-center gap-2 text-center">
      <Skeleton className="h-7 w-56" />
      <Skeleton className="h-4 w-72" />
    </div>
  )
}

/** A rank option row, shaped like RANK_ROW_CLASS: text on the left, the size-44 indicator on the right. */
function RankRowSkeleton() {
  return (
    <div className={RANK_ROW_CLASS}>
      <Skeleton className="h-4 flex-1" />
      <Skeleton className="size-11 shrink-0 rounded-full" />
    </div>
  )
}

/** Pending UI for a test step (`<Test>`): steps + header + the flow-frame box with progress, three
 *  rank rows and the Back/Next buttons. */
export function TestStepPending() {
  return (
    <FlowShell>
      <FlowStepsSkeleton />
      <FlowHeaderSkeleton />
      <FlowFrame contentClassName="flex flex-col gap-5">
        {/* Progress (count/percent row + track) */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-8" />
          </div>
          <Skeleton className="h-1 w-full rounded-full" />
        </div>
        {/* Three rank options */}
        <div className="flex flex-col gap-2">
          <RankRowSkeleton />
          <RankRowSkeleton />
          <RankRowSkeleton />
        </div>
        {/* Back / Next */}
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-9 w-32 rounded-md" />
          <Skeleton className="h-9 w-28 rounded-md" />
        </div>
      </FlowFrame>
    </FlowShell>
  )
}

/** Pending UI for the test intro (test.index): steps + header + the two "how it works" / "what you get"
 *  card lists + the CTA. No flow-frame box here — the intro morphs via `flow-header` only. */
export function IntroStepPending() {
  return (
    <FlowShell>
      <FlowStepsSkeleton />
      <FlowHeaderSkeleton />
      {[0, 1].map((section) => (
        <div key={section} className="flex w-full flex-col gap-3">
          <Skeleton className="h-3 w-24" />
          <ul className="flex flex-col gap-3">
            {(section === 0 ? [0, 1, 2] : [0]).map((i) => (
              <li key={i} className="bg-card flex items-center gap-4 rounded-xl border p-4">
                <Skeleton className="size-12 shrink-0 rounded-xl" />
                <div className="flex flex-1 flex-col gap-1.5">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-full max-w-[16rem]" />
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <div className="flex w-full flex-col gap-2">
        <Skeleton className="h-12 w-full rounded-md" />
        <Skeleton className="mx-auto h-3 w-56" />
      </div>
    </FlowShell>
  )
}

export function RegisterChoicePending() {
  return (
    <AuthLayout>
      <div className="mx-auto flex w-full max-w-[480px] flex-col items-center gap-8 p-6">
        <Skeleton className="h-7 w-32" />
        <div className="flow-header flex flex-col items-center gap-2 text-center">
          <Skeleton className="h-8 w-52" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="flow-content flex w-full flex-col gap-4 sm:flex-row">
          {[0, 1].map((i) => (
            <Frame key={i} className="flex-1">
              <div className="flex flex-col items-center gap-4 px-4 py-2 text-center">
                <Skeleton className="size-14 rounded-full" />
                <div className="flex flex-col items-center gap-1.5">
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-3 w-40" />
                </div>
                <Skeleton className="h-4 w-24" />
              </div>
            </Frame>
          ))}
        </div>
        <Skeleton className="h-4 w-44" />
      </div>
    </AuthLayout>
  )
}
